import React from 'react';
import PropTypes from 'prop-types';
import InternetBankingActions from '../../../actions/InternetBankingActions';
import StepHeader from '../../../includes/StepHeader';
import { Title } from 'web-ui-components/lib/atoms/text';
import { Divider } from 'web-ui-components/lib/atoms/layout';
import { Field, Search } from 'web-ui-components/lib/molecules/forms';
import { SubBody } from 'web-ui-components/lib/typography/body';
import AccountPoolsActionCreator from '../../../actions/AccountPoolsActionCreator';
import AccountPoolsStore from '../../../stores/AccountPoolsStore';
import { RadioButtonGroup } from '../RadioButtonGroup/RadioButtonGroup';
import { Button } from 'web-ui-components/lib/atoms/buttons';
import { InputField } from '../InputField/InputField';
import {
  SubHeading,
  SubsectionHeading,
} from 'web-ui-components/lib/typography/headings';
import { AccountCardList } from '../AccountCardList/AccountCardList';
import { LeftAndRightAlignment } from 'web-ui-components/lib/global/layout';
import { AccountPoolFooter } from '../AccountPoolFooter/AccountPoolFooter';
import { NotionalCurrencySelect } from '../NotionalCurrencySelect/NotionalCurrencySelect';
import {
  validateField,
  getErrorMessage,
  removeError,
  validateFields,
} from '../selectors/errors';
import AccountPoolsConstant from '../../../constants/AccountPoolsConstant.js';
import { ConfirmationModal } from 'web-ui-components/lib/organisms/modals';

class CreateAccountPool extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      notionalCurrency: null,
      popularNotionalCurrency: null,
      accountPoolName: '',
      editMode: AccountPoolsStore.isEditMode(),
      selectedAccounts: [],
      accountPoolsLoaded: AccountPoolsStore.isAccountLoaded(),
      accounts: AccountPoolsStore.getAllAccounts(),
      isPoolSubmitted: false,
      onSubmitClick: false,
      errors: [],
      poolId: null,
      deletePoolConfirmationModal: false,
    };
  }

  componentDidMount() {
    const { editMode, accounts: accountList } = this.state;
    AccountPoolsStore.addChangeListener(this.onChangeAccountPoolStore);
    if (editMode) {
      const { poolName, currency, accounts, poolId } =
        AccountPoolsStore.getEditPoolDetails();
      this.setState({
        popularNotionalCurrency: this.isPopularCurrency(currency)
          ? currency
          : 'other',
        notionalCurrency: !this.isPopularCurrency(currency) && currency,
        accountPoolName: poolName,
        selectedAccounts: accounts,
        poolId: poolId,
      });
      this.setState({
        accounts: this.arrangeAccountListInEditMode(
          accountList,
          accounts,
          editMode
        ),
      });
    }
  }
  componentWillUnmount() {
    AccountPoolsStore.removeChangeListener(this.onChangeAccountPoolStore);
  }

  isPopularCurrency(currency) {
    const {
      content: { popularCurrencies },
    } = this.props;
    return popularCurrencies.includes(currency);
  }

  onChangeAccountPoolStore = () => {
    const { accounts, editMode } = this.state;
    this.setState({
      accountPoolsLoaded: !accounts
        ? AccountPoolsStore.isAccountLoaded()
        : true,
      accounts: AccountPoolsStore.getAllAccounts(),
      isPoolSubmitted: !editMode
        ? AccountPoolsStore.isPoolSubmitted()
        : AccountPoolsStore.isPoolUpdated(),
    });
  };

  selectedAccountList = (accountId) => {
    const { selectedAccounts } = this.state;
    this.setState({
      selectedAccounts:
        selectedAccounts.indexOf(accountId) === -1
          ? [...selectedAccounts, accountId]
          : selectedAccounts.filter((account) => account !== accountId),
    });
  };
  searchResult = (accounts, searchTerm) => {
    return accounts.filter(
      ({ number, metadata: { account_name }, product: { name } }) =>
        (name && name.toLowerCase().indexOf(searchTerm) > -1) ||
        (account_name && account_name.toLowerCase().indexOf(searchTerm) > -1) ||
        number.indexOf(searchTerm) > -1
    );
  };

  searchAccounts = (searchTerm) => {
    const { accounts, selectedAccounts, editMode } = this.state;
    this.setState({
      accounts: !searchTerm
        ? this.arrangeAccountListInEditMode(
            AccountPoolsStore.getAllAccounts(),
            selectedAccounts,
            editMode
          )
        : this.searchResult(accounts, searchTerm),
    });
  };

  submitAccountPool = () => {
    const { editMode } = this.state;
    const { content } = this.props;
    const errors = validateFields(this.state, content);
    this.setState({ errors });
    if (errors.length === 0) {
      this.setState({ onSubmitClick: true }, () =>
        !editMode
          ? AccountPoolsActionCreator.createAccountPool(this.state)
          : AccountPoolsActionCreator.updateAccountPool(this.state)
      );
    }
  };

  cancelOrBack = () => {
    this.setState({ selectedAccounts: [] });
    InternetBankingActions.navigateToWebTask('WEB-ACCOUNT-POOLS');
  };

  onPoolNameBlur = (e) => {
    const { errors } = this.state;
    const { content } = this.props;
    const {
      target: { value },
    } = e;
    this.setState({
      errors: validateField(
        errors,
        value,
        content,
        AccountPoolsConstant.POOL_NAME_KEY
      ),
    });
  };

  onPoolNameChange = (e) => {
    const { errors } = this.state;
    const {
      target: { value },
    } = e;
    this.setState({
      accountPoolName: value,
      errors: removeError(AccountPoolsConstant.POOL_NAME_KEY, errors),
    });
  };

  onPopularCurrencyChange = (e) => {
    const { errors } = this.state;
    this.setState({
      popularNotionalCurrency: e,
      errors: removeError(AccountPoolsConstant.POPULAR_CURRENCY_KEY, errors),
    });
  };

  onCurrencySelect = (id) => {
    const { errors } = this.state;
    this.setState({
      notionalCurrency: id,
      errors: removeError(AccountPoolsConstant.CURRENCY_SELECT_KEY, errors),
    });
  };

  deletePool = () => {
    this.setState({
      deletePoolConfirmationModal: true,
    });
  };

  deleteAccountPoolConfirm = () => {
    this.setState({
      deletePoolConfirmationModal: false,
    });
    AccountPoolsActionCreator.deleteAccountPool();
  };

  arrangeAccountListInEditMode = (accountList, selectedAccounts, editMode) => {
    if (editMode) {
      const restAccountList = accountList.filter((account) => {
        return !selectedAccounts.includes(account.id);
      });
      const preSelectedAccounts = accountList.filter((item) => {
        return selectedAccounts.includes(item.id);
      });
      return [...preSelectedAccounts, ...restAccountList];
    } else {
      return accountList;
    }
  };

  render() {
    const {
      content: {
        createPoolButtonText,
        savePoolButtonText,
        cancelButton,
        currencies,
        accountsSelected,
        createAccountPool,
        editAccountPool,
        nameofPool,
        deletePool,
        deletePoolModalTitle,
        dismissDeleteAccountPoolButton,
        confirmDeleteAccountPoolButton,
        deletePoolModalBody,
        selectAccountCreateMsg,
        selectAccountEditMsg,
        chooseAccountValidationMessage,
        yourAccountType: { fieldHeader, fieldOptions },
      },
    } = this.props;
    const {
      popularNotionalCurrency,
      notionalCurrency,
      accounts,
      selectedAccounts,
      accountPoolName,
      editMode,
      isPoolSubmitted,
      onSubmitClick,
      errors,
      deletePoolConfirmationModal,
    } = this.state;
    return (
      <Field>
        <Field className="page container">
          <Field className="col-xs1-20">
            {isPoolSubmitted &&
              InternetBankingActions.navigateToWebTask('WEB-ACCOUNT-POOLS')}
            <StepHeader
              showheaderCrumbs
              onClick={this.cancelOrBack}
              headerCrumbsMessage="Back"
              controlBack={this.back}
            />
            <Field className="web-ui-components">
              <LeftAndRightAlignment
                leftNode={
                  <Title className="u-margin-bottom-0" size="h1">
                    {!editMode ? createAccountPool : editAccountPool}
                  </Title>
                }
                rightNode={
                  editMode && (
                    <Button display="primary" onClick={this.deletePool}>
                      {deletePool}
                    </Button>
                  )
                }
              />
            </Field>
            <InputField
              name="poolName"
              title={nameofPool}
              value={accountPoolName}
              maxLength={AccountPoolsConstant[30]}
              size="35"
              onFieldBlur={(e) => this.onPoolNameBlur(e)}
              validationMessage={getErrorMessage(
                AccountPoolsConstant.POOL_NAME_KEY,
                errors
              )}
              onChange={(e) => this.onPoolNameChange(e)}
            />
            <SubBody>
              <span>
                Characters:{' '}
                {AccountPoolsConstant[30] - Number(accountPoolName.length)} of{' '}
                {AccountPoolsConstant[30]} left
              </span>
            </SubBody>
            <RadioButtonGroup
              name="popularNotionalCurreny"
              title={fieldHeader}
              errorMessage={getErrorMessage(
                AccountPoolsConstant.POPULAR_CURRENCY_KEY,
                errors
              )}
              options={fieldOptions}
              selectedOption={popularNotionalCurrency}
              fullWidth
              onChange={(e) => this.onPopularCurrencyChange(e)}
            />
            {popularNotionalCurrency === 'other' && (
              <NotionalCurrencySelect
                currencies={currencies}
                selectedId={notionalCurrency}
                validationMessage={getErrorMessage(
                  AccountPoolsConstant.CURRENCY_SELECT_KEY,
                  errors
                )}
                onCurrencySelect={(id) => this.onCurrencySelect(id)}
              />
            )}
            <Divider />

            <Field className="web-ui-components">
              <LeftAndRightAlignment
                leftNode={
                  <SubHeading>
                    {editMode ? selectAccountEditMsg : selectAccountCreateMsg}
                  </SubHeading>
                }
                rightNode={
                  <Search
                    onChange={(searchTerm) =>
                      this.searchAccounts(searchTerm.toLowerCase())
                    }
                  />
                }
              />

              <SubsectionHeading>
                {chooseAccountValidationMessage}
              </SubsectionHeading>
            </Field>
            <AccountCardList
              accountList={accounts}
              selectedAccountList={this.selectedAccountList}
              selectedAccounts={selectedAccounts}
            />
          </Field>
          <Field>
            {deletePoolConfirmationModal && (
              <ConfirmationModal
                title={deletePoolModalTitle}
                onConfirm={this.deleteAccountPoolConfirm}
                isOpen={deletePoolConfirmationModal}
                onClose={() =>
                  this.setState({ deletePoolConfirmationModal: false })
                }
                dismissButtonText={dismissDeleteAccountPoolButton}
                confirmButtonText={confirmDeleteAccountPoolButton}
              >
                <p>{deletePoolModalBody}</p>
              </ConfirmationModal>
            )}
          </Field>
        </Field>
        {selectedAccounts.length > 1 &&
          selectedAccounts.length < AccountPoolsConstant[101] && (
            <AccountPoolFooter
              submitAccountPool={this.submitAccountPool}
              cancelbuttonText={cancelButton}
              cancelButton={this.cancelOrBack}
              saveButtonText={
                !editMode ? createPoolButtonText : savePoolButtonText
              }
              selectAccountText={accountsSelected}
              selectedCount={selectedAccounts ? selectedAccounts.length : 0}
              isLoading={onSubmitClick}
            />
          )}
      </Field>
    );
  }
}
CreateAccountPool.propTypes = {
  content: PropTypes.shape({
    content: PropTypes.string,
  }).isRequired,
};
export default CreateAccountPool;
